import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import {
  FiActivity, FiServer, FiDatabase, FiCpu, FiHardDrive,
  FiWifi, FiAlertCircle, FiCheckCircle, FiRefreshCw, FiTrendingUp,
  FiTrendingDown, FiClock, FiZap
} from 'react-icons/fi';
import { Line, Doughnut } from 'react-chartjs-2';
import 'chart.js/auto';

// Composant Gauge circulaire
const CircularGauge = ({ value, max = 100, label, color = 'yellow', size = 120 }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (color === 'yellow') {
      if (percentage < 50) return '#10b981'; // green
      if (percentage < 80) return '#fbbf24'; // yellow
      return '#ef4444'; // red
    }
    return color;
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r="45"
            stroke="rgba(75, 85, 99, 0.3)"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r="45"
            stroke={getColor()}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{Math.round(value)}</div>
            {max !== 100 && <div className="text-xs text-gray-400">/ {max}</div>}
            {max === 100 && <div className="text-xs text-gray-400">%</div>}
          </div>
        </div>
      </div>
      <div className="mt-2 text-sm text-gray-400 text-center">{label}</div>
    </div>
  );
};

// Composant Metric Card amélioré
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'yellow', children }) => {
  const getColorClasses = () => {
    const colors = {
      yellow: 'border-yellow-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      green: 'border-green-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      red: 'border-red-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      blue: 'border-blue-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
      purple: 'border-purple-600/30 bg-gradient-to-br from-gray-800 to-gray-900',
    };
    return colors[color] || colors.yellow;
  };

  const getIconColor = () => {
    const colors = {
      yellow: 'text-yellow-500',
      green: 'text-green-500',
      red: 'text-red-500',
      blue: 'text-blue-500',
      purple: 'text-purple-500',
    };
    return colors[color] || colors.yellow;
  };

  return (
    <div className={`rounded-xl border ${getColorClasses()} p-6 shadow-lg backdrop-blur-sm`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-gray-700/50 ${getIconColor()}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? <FiTrendingUp className="w-4 h-4 mr-1" /> : <FiTrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <div className="text-sm text-gray-400 font-medium">{title}</div>
        <div className="text-3xl font-bold text-white">{value}</div>
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
};

function AdminSystemMonitoring() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [history, setHistory] = useState({
    cpu: [],
    memory: [],
    disk: [],
    apiRequests: [],
    apiResponseTime: [],
    dbConnections: []
  });
  const historySize = 30; // Garder les 30 dernières valeurs

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.getSystemMetrics();
      if (response && response.success) {
        const data = response.data;
        setMetrics(data);
        setLastUpdate(new Date());

        // Mettre à jour l'historique pour les graphiques
        setHistory(prev => ({
          cpu: [...prev.cpu.slice(-historySize + 1), data.server.cpu.usage],
          memory: [...prev.memory.slice(-historySize + 1), 
            (data.server.memory.used / data.server.memory.total) * 100],
          disk: [...prev.disk.slice(-historySize + 1),
            (data.server.disk.used / data.server.disk.total) * 100],
          apiRequests: [...prev.apiRequests.slice(-historySize + 1), data.api.requestsPerMinute],
          apiResponseTime: [...prev.apiResponseTime.slice(-historySize + 1), data.api.averageResponseTime],
          dbConnections: [...prev.dbConnections.slice(-historySize + 1),
            (data.database.connections / data.database.maxConnections) * 100]
        }));
      } else {
        throw new Error(response?.message || 'Erreur lors du chargement des métriques');
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const isOnline = status === 'online' || status === 'connected';
    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        isOnline ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
      }`}>
        <div className={`w-2 h-2 rounded-full mr-2 ${isOnline ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
        {isOnline ? 'En ligne' : 'Hors ligne'}
      </div>
    );
  };

  // Configuration des graphiques
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.95)',
        titleColor: '#fbbf24',
        bodyColor: '#e5e7eb',
        borderColor: 'rgba(234, 179, 8, 0.5)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#9ca3af',
          font: {
            size: 10
          },
          maxTicksLimit: 6
        }
      },
      y: {
        grid: {
          color: 'rgba(75, 85, 99, 0.2)'
        },
        ticks: {
          color: '#9ca3af',
          font: {
            size: 10
          }
        },
        beginAtZero: true
      }
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 4
      },
      line: {
        borderWidth: 2,
        tension: 0.4
      }
    }
  };

  // Données pour les graphiques de tendances
  const cpuChartData = {
    labels: history.cpu.map((_, i) => ''),
    datasets: [{
      label: 'CPU Usage (%)',
      data: history.cpu,
      borderColor: '#fbbf24',
      backgroundColor: 'rgba(251, 191, 36, 0.1)',
      fill: true
    }]
  };

  const memoryChartData = {
    labels: history.memory.map((_, i) => ''),
    datasets: [{
      label: 'Memory Usage (%)',
      data: history.memory,
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true
    }]
  };

  const apiChartData = {
    labels: history.apiRequests.map((_, i) => ''),
    datasets: [{
      label: 'Requests/min',
      data: history.apiRequests,
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true
    }]
  };

  const responseTimeChartData = {
    labels: history.apiResponseTime.map((_, i) => ''),
    datasets: [{
      label: 'Response Time (ms)',
      data: history.apiResponseTime,
      borderColor: '#8b5cf6',
      backgroundColor: 'rgba(139, 92, 246, 0.1)',
      fill: true
    }]
  };

  // Données pour les graphiques en donut
  const memoryDonutData = metrics ? {
    labels: ['Utilisé', 'Libre'],
    datasets: [{
      data: [
        (metrics.server.memory.used / metrics.server.memory.total) * 100,
        100 - (metrics.server.memory.used / metrics.server.memory.total) * 100
      ],
      backgroundColor: [
        (metrics.server.memory.used / metrics.server.memory.total) * 100 < 50 ? '#10b981' :
        (metrics.server.memory.used / metrics.server.memory.total) * 100 < 80 ? '#fbbf24' : '#ef4444',
        'rgba(75, 85, 99, 0.3)'
      ],
      borderWidth: 0
    }]
  } : null;

  const diskDonutData = metrics ? {
    labels: ['Utilisé', 'Libre'],
    datasets: [{
      data: [
        (metrics.server.disk.used / metrics.server.disk.total) * 100,
        100 - (metrics.server.disk.used / metrics.server.disk.total) * 100
      ],
      backgroundColor: [
        (metrics.server.disk.used / metrics.server.disk.total) * 100 < 50 ? '#10b981' :
        (metrics.server.disk.used / metrics.server.disk.total) * 100 < 80 ? '#fbbf24' : '#ef4444',
        'rgba(75, 85, 99, 0.3)'
      ],
      borderWidth: 0
    }]
  } : null;

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '75%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(31, 41, 55, 0.95)',
        titleColor: '#fbbf24',
        bodyColor: '#e5e7eb',
        borderColor: 'rgba(234, 179, 8, 0.5)',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            return context.label + ': ' + context.parsed.toFixed(1) + '%';
          }
        }
      }
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
            <FiActivity className="w-8 h-8 mr-3 text-yellow-500" />
            Monitoring système
          </h1>
          <p className="text-gray-400">Surveillance en temps réel de l'infrastructure</p>
        </div>
        <div className="flex items-center space-x-4">
          {lastUpdate && (
            <div className="text-sm text-gray-400 flex items-center">
              <FiClock className="w-4 h-4 mr-2" />
              Dernière mise à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
            </div>
          )}
          <button
            onClick={loadMetrics}
            disabled={loading}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-all flex items-center shadow-lg hover:shadow-xl disabled:opacity-50"
          >
            <FiRefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {loading && !metrics ? (
        <div className="p-12 text-center">
          <div className="inline-block w-12 h-12 border-4 border-t-yellow-500 border-yellow-500/30 rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">Chargement des métriques...</p>
        </div>
      ) : metrics ? (
        <>
          {/* Status Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Serveur"
              value={metrics.server.status === 'online' ? 'En ligne' : 'Hors ligne'}
              icon={FiServer}
              color={metrics.server.status === 'online' ? 'green' : 'red'}
            >
              {getStatusBadge(metrics.server.status)}
            </MetricCard>
            <MetricCard
              title="Base de données"
              value={metrics.database.status === 'connected' ? 'Connectée' : 'Déconnectée'}
              icon={FiDatabase}
              color={metrics.database.status === 'connected' ? 'green' : 'red'}
            >
              {getStatusBadge(metrics.database.status)}
            </MetricCard>
            <MetricCard
              title="Uptime"
              value={metrics.server.uptime}
              icon={FiClock}
              color="blue"
            />
            <MetricCard
              title="Requêtes API/min"
              value={metrics.api.requestsPerMinute}
              subtitle={`${metrics.api.averageResponseTime}ms avg`}
              icon={FiZap}
              color="purple"
            />
          </div>

          {/* Serveur - CPU, Memory, Disk avec graphiques */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <FiServer className="w-6 h-6 mr-3 text-yellow-500" />
                Serveur
              </h2>
              {getStatusBadge(metrics.server.status)}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* CPU */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-gray-300">
                    <FiCpu className="w-5 h-5 mr-2 text-yellow-500" />
                    <span className="font-medium">CPU</span>
                  </div>
                  <span className="text-2xl font-bold text-white">{metrics.server.cpu.usage}%</span>
                </div>
                <div className="h-32">
                  <Line data={cpuChartData} options={chartOptions} />
                </div>
                <div className="mt-2 text-xs text-gray-400 text-center">
                  {metrics.server.cpu.cores} cœurs disponibles
                </div>
              </div>

              {/* Memory */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-gray-300">
                    <FiHardDrive className="w-5 h-5 mr-2 text-blue-500" />
                    <span className="font-medium">Mémoire</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-white">
                      {metrics.server.memory.used} / {metrics.server.memory.total} {metrics.server.memory.unit}
                    </span>
                    <div className="text-xs text-gray-400">
                      {Math.round((metrics.server.memory.used / metrics.server.memory.total) * 100)}%
                    </div>
                  </div>
                </div>
                <div className="h-32">
                  <Line data={memoryChartData} options={chartOptions} />
                </div>
              </div>

              {/* Disk */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-gray-300">
                    <FiHardDrive className="w-5 h-5 mr-2 text-green-500" />
                    <span className="font-medium">Disque</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-white">
                      {metrics.server.disk.used} / {metrics.server.disk.total} {metrics.server.disk.unit}
                    </span>
                    <div className="text-xs text-gray-400">
                      {Math.round((metrics.server.disk.used / metrics.server.disk.total) * 100)}%
                    </div>
                  </div>
                </div>
                <div className="h-32 flex items-center justify-center">
                  {diskDonutData && (
                    <Doughnut data={diskDonutData} options={donutOptions} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Base de données */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <FiDatabase className="w-6 h-6 mr-3 text-blue-500" />
                Base de données PostgreSQL
              </h2>
              {getStatusBadge(metrics.database.status)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                title="Connexions"
                value={`${metrics.database.connections} / ${metrics.database.maxConnections}`}
                subtitle={`${Math.round((metrics.database.connections / metrics.database.maxConnections) * 100)}% utilisé`}
                icon={FiActivity}
                color="blue"
              />
              <MetricCard
                title="Requêtes/seconde"
                value={metrics.database.queriesPerSecond}
                icon={FiZap}
                color="purple"
              />
              <MetricCard
                title="Taille"
                value={metrics.database.size}
                icon={FiHardDrive}
                color="green"
              />
              <MetricCard
                title="Requêtes actives"
                value={metrics.database.activeQueries || 0}
                subtitle={`${metrics.database.waitingQueries || 0} en attente`}
                icon={FiActivity}
                color="yellow"
              />
            </div>
          </div>

          {/* API Performance */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-purple-600/30 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <FiWifi className="w-6 h-6 mr-3 text-purple-500" />
                Performance API
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Graphique des requêtes */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-gray-300">
                    <FiActivity className="w-5 h-5 mr-2 text-green-500" />
                    <span className="font-medium">Requêtes par minute</span>
                  </div>
                  <span className="text-xl font-bold text-white">{metrics.api.requestsPerMinute}</span>
                </div>
                <div className="h-48">
                  <Line data={apiChartData} options={chartOptions} />
                </div>
              </div>

              {/* Graphique du temps de réponse */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center text-gray-300">
                    <FiZap className="w-5 h-5 mr-2 text-purple-500" />
                    <span className="font-medium">Temps de réponse moyen</span>
                  </div>
                  <span className="text-xl font-bold text-white">{metrics.api.averageResponseTime}ms</span>
                </div>
                <div className="h-48">
                  <Line data={responseTimeChartData} options={chartOptions} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <MetricCard
                title="Requêtes/min"
                value={metrics.api.requestsPerMinute}
                icon={FiActivity}
                color="green"
              />
              <MetricCard
                title="Temps de réponse"
                value={`${metrics.api.averageResponseTime}ms`}
                icon={FiZap}
                color="purple"
              />
              <MetricCard
                title="Taux d'erreur"
                value={`${metrics.api.errorRate}%`}
                icon={FiAlertCircle}
                color={metrics.api.errorRate < 1 ? 'green' : 'red'}
              />
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                <div className="text-sm text-gray-400 mb-3">Codes de statut</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">200:</span>
                    <span className="text-green-400 font-bold">{metrics.api.statusCode200}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">400:</span>
                    <span className="text-yellow-400 font-bold">{metrics.api.statusCode400}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">500:</span>
                    <span className="text-red-400 font-bold">{metrics.api.statusCode500}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cache */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-600/30 rounded-xl p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <FiActivity className="w-6 h-6 mr-3 text-gray-400" />
                Cache
              </h2>
              {metrics.cache.status === 'not_configured' ? (
                <span className="px-3 py-1 text-xs bg-gray-700 text-gray-400 rounded-full border border-gray-600">
                  Non configuré
                </span>
              ) : (
                getStatusBadge(metrics.cache.status)
              )}
            </div>

            {metrics.cache.status === 'not_configured' ? (
              <div className="text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700/50">
                <FiActivity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-300 text-lg font-medium mb-2">Aucun système de cache configuré</p>
                <p className="text-gray-500 text-sm">Configurez Redis pour activer le cache et améliorer les performances</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MetricCard
                  title="Taux de réussite"
                  value={`${metrics.cache.hitRate}%`}
                  icon={FiActivity}
                  color={metrics.cache.hitRate > 80 ? 'green' : metrics.cache.hitRate > 50 ? 'yellow' : 'red'}
                />
                <MetricCard
                  title="Utilisation mémoire"
                  value={`${metrics.cache.memoryUsage} ${metrics.cache.unit}`}
                  icon={FiHardDrive}
                  color="blue"
                />
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default AdminSystemMonitoring;
